import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';

const AlertaVigilancia = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const alertaId = searchParams.get('id');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [codigo, setCodigo] = useState('');
  const [segundos, setSegundos] = useState(0);

  const cargar = useCallback(async () => {
    if (!alertaId || !user?.username) {
      setError('Falta el identificador de la alerta o la sesión.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const url = new URL(getApiUrl('ALERTA_VIGILANCIA', 'BY_ID').replace(':id', encodeURIComponent(alertaId)));
      url.searchParams.set('username', user.username);
      const res = await fetch(url.toString());
      const json = await res.json();
      if (!res.ok || !json.success) {
        setError(json.error || 'No se pudo cargar la alerta.');
        setData(null);
        return;
      }
      setData(json.data);
      setSegundos(Number(json.data.segundos_restantes || 0));
    } catch (e) {
      setError(e.message || 'Error de conexión.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [alertaId, user?.username]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    if (!data?.disponible || segundos <= 0) return undefined;
    const t = setInterval(() => {
      setSegundos((s) => {
        if (s <= 1) {
          clearInterval(t);
          setTimeout(() => cargar(), 0);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [data?.disponible, data?.id, cargar]);

  const tiempoTexto = useMemo(() => {
    const m = Math.floor(segundos / 60);
    const s = segundos % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }, [segundos]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!codigo.trim() || submitting || !data?.disponible) return;

    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(
        getApiUrl('ALERTA_VIGILANCIA', 'VALIDAR').replace(':id', encodeURIComponent(alertaId)),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: user.username,
            codigo: codigo.trim()
          })
        }
      );
      const json = await res.json();
      const result = json.data || json;

      if (result.imagen) {
        setData((prev) => ({
          ...(prev || {}),
          ...result,
          disponible: result.estado === 'activa' && (result.intentos_restantes || 0) > 0,
          imagen: result.imagen,
          intentos_restantes: result.intentos_restantes,
          mensaje: result.mensaje
        }));
      } else {
        setData((prev) => ({
          ...(prev || {}),
          estado: result.estado,
          disponible: false,
          mensaje: result.mensaje,
          intentos_restantes: result.intentos_restantes ?? 0,
          imagen: null
        }));
      }

      if (result.success) {
        setCodigo('');
      } else {
        setCodigo('');
        if (!result.success && result.mensaje) setError(result.mensaje);
      }
    } catch (err) {
      setError(err.message || 'Error al validar.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
        <p className="text-sm text-gray-600">Cargando alerta...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md bg-white shadow-md border border-gray-200 rounded-lg p-5 sm:p-6">
        <h1 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Alerta de vigilancia</h1>
        <p className="text-sm text-gray-600 mb-4">
          Complete el código dentro del tiempo indicado.
        </p>

        {data && (
          <div className="mb-4 flex items-center justify-between text-sm">
            <span className="text-gray-700">
              Estado: <strong>{data.estado}</strong>
            </span>
            {data.disponible && (
              <span className={`font-mono font-semibold ${segundos <= 60 ? 'text-red-600' : 'text-indigo-700'}`}>
                {tiempoTexto}
              </span>
            )}
          </div>
        )}

        {error && (
          <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {data?.mensaje && !data.disponible && (
          <div className="mb-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {data.mensaje}
          </div>
        )}

        {data?.disponible && data?.imagen && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <p className="text-sm font-medium text-red-900 mb-2">Introduzca el código de la imagen:</p>
              <div className="flex justify-center mb-3 rounded border border-gray-200 bg-gray-50 p-3">
                <img src={data.imagen} alt="CAPTCHA" className="max-w-full h-auto" />
              </div>
              <input
                type="text"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                autoComplete="off"
                autoCapitalize="characters"
                className="w-full border border-gray-300 rounded px-3 py-2 text-center tracking-widest text-lg uppercase focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Código"
                maxLength={8}
              />
              <p className="mt-1 text-xs text-gray-500">
                Intentos restantes: {data.intentos_restantes ?? 0} / {data.max_intentos ?? 2}
              </p>
            </div>
            <button
              type="submit"
              disabled={submitting || !codigo.trim()}
              className="w-full rounded bg-indigo-600 text-white py-2.5 text-sm font-semibold disabled:opacity-50"
            >
              {submitting ? 'Validando...' : 'Validar código'}
            </button>
          </form>
        )}

        {/* Volver solo si la actividad ya no está disponible (vencida / ok / sin intentos). */}
        {!loading && data && !data.disponible && (
          <button
            type="button"
            onClick={() => navigate(user?.rol === 'S' ? '/dashboard-supervision' : '/dashboard')}
            className="mt-5 w-full rounded border border-gray-300 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Volver
          </button>
        )}
      </div>
    </div>
  );
};

export default AlertaVigilancia;
